/* eslint-disable no-void */

import * as vp from 'vopay';

import { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import vopayApi from '../../services/api.config';
import { useDataFetchIndicator } from '..';

type HookProps = {
  api: vp.Api;
};

type ReturnTuple = [
  vp.FinancialInstitutionBranchesResponse,
  (_params: vp.FinancialInstitutionBranchesRequest) => void,
  () => void,
  (_params: vp.FinancialInstitutionBranchesRequest) => Promise<vp.FinancialInstitutionBranchesResponse>,
];

function useFetchFinancialInstitutionBranches({ api }: HookProps): ReturnTuple {
  const { t } = useTranslation('translations', { keyPrefix: 'Common' });
  const source = axios.CancelToken.source();

  const [params, setParams] = useState<vp.FinancialInstitutionBranchesRequest>();
  const [response, setResponse] = useState<vp.FinancialInstitutionBranchesResponse>({
    Success: false,
    ErrorMessage: '',
    Branches: [],
  });
  const [shouldSendRequest, setSendRequest] = useState(false);
  const [, setDataFetchingInfo] = useDataFetchIndicator();

  useEffect(() => {
    const sendRequest = async (requestParams: vp.FinancialInstitutionBranchesRequest) => {
      setDataFetchingInfo(true, `${t('defaultLoading')}`, 'fullScreen');

      const res = await api.fetchFinancialInstitutionBranches(requestParams, source);

      setResponse(res);
      setSendRequest(false);
      setDataFetchingInfo(false);

      return () => {};
    };

    if (params && shouldSendRequest) void sendRequest(params);

    return () => {
      source.cancel();
    };
  }, [shouldSendRequest]);

  return [
    response,
    (_params: vp.FinancialInstitutionBranchesRequest) => {
      setParams(_params);
      setSendRequest(true);
    },
    () => {
      setResponse({
        Success: false,
        ErrorMessage: '',
        Branches: [],
      });
      setSendRequest(false);
    },
    async (_params: vp.FinancialInstitutionBranchesRequest) => {
      try {
        setDataFetchingInfo(true, `${t('defaultLoading')}`, 'fullScreen');
        const res = await api.fetchFinancialInstitutionBranches(_params, source);

        return res;
      } finally {
        setDataFetchingInfo(false);
      }
    },
  ];
}

export default vopayApi.withApiHook<HookProps, ReturnTuple>(useFetchFinancialInstitutionBranches);
